<template>
    <!-- MAIN MAP -->
    <div class="map">
        <l-map ref="map" v-model:zoom="zoom" v-model:center="localCenter" :use-global-leaflet="true" tabindex="0"
            @contextmenu="addMarker" @mousemove="mouseOverMap" @update:bounds="boundsChanged" :max-zoom="22"
            zoom-control="false" @ready="onMapReady" @click="handleMapClick">
            <!-- Capa base del mapa -->
            <l-tile-layer ref="tileLayer" :url="layer.url" :attribution="layer.attribution" layer-type="base"
                :name="layer.name"></l-tile-layer>

            <!-- Capas GeoJSON -->
            <l-geo-json v-if="prediosLayer" :geojson="predios" :options-style="getLayerStyle('predios')"
                @mouseover="handleMouseOver" @mouseout="handleMouseOut"></l-geo-json>

            <l-geo-json v-if="regionesLayer" :geojson="regiones"
                :options-style="getLayerStyle('regiones')"></l-geo-json>

            <l-geo-json v-if="comunasLayer" :geojson="comunas" :options-style="getLayerStyle('comunas')"></l-geo-json>

            <l-geo-json v-if="ciudadesLayer" :geojson="ciudades"
                :options-style="getLayerStyle('ciudades')"></l-geo-json>

            <l-geo-json v-if="carreterasLayer && carreteras" :geojson="carreteras"
                :options-style="getLayerStyle('carreteras')"></l-geo-json>

            <!-- Marcadores de eventos -->
            <template v-if="isEvents">
                <l-marker v-for="evt in filteredEvents" :key="evt.event_id" :title="evt.event_id"
                    :lat-lng="[evt.location.coordinates[1], evt.location.coordinates[0]]"
                    @click="selectMarker(evt.event_id)">
                    <l-icon :icon-size="iconSize" :icon-anchor="iconAnchor" class-name="point-event">
                        <span class="point-head">{{ evt.event_unit + ' ' + evt.event_number }}</span>
                        <div class="point-icon">
                            <span>{{ evt.scope.substring(0, 1) }}</span>
                            <span class="icono-wrapper">
                                <img v-if="useUtils().getEventTypeId(evt.type) > 100" class="fondo-icono" :src="'/images/' +  useUtils().getEventBg(evt.type) + '.png'" alt="Fondo especial" />
                                <img  class="icono" :src="'/images/event_type_' + getTypeId(evt.type) + '.png'"
                                    :alt="'Event type ' + evt.type">
                            </span>
                        </div>
                    </l-icon>
                    <l-tooltip>
                        {{ evt.type }}<br>
                        {{ evt.utc }}
                    </l-tooltip>
                </l-marker>
            </template>
            <OwnEventsMarkers @eventSelected="selectOwnMarker" />
            <!-- Marcadores de photos -->
            <PhotoMarkers v-if="loadedPhotos && (isImageLibrary || isSearchModule)" :photos="photoFiles" :isImageLibrary="isImageLibrary || isSearchModule"
                :map="map" />

            <!-- Marcadores de mosaicos -->
             <template v-for="mosaic in latestMosaics" >
                <MosaicMarkers v-if="viewLatestMosaic && showLatestMosaicPermission" :photoDetails="getMosaicPhotos(mosaic.mosaic_id)" 
                v-bind:key=" mosaic.mosaic_id" :map="map" />
            </template>
            <MosaicMarkers v-if="selectedMosaicId !== null && isMosaicLibrary && selectedMosaicPhotos.length > 0" 
                :photoDetails="selectedMosaicPhotos" 
                v-bind:key="'h' + selectedMosaicId" :map="map" />
            <!-- Marcadores de videos -->
            <VideoMarkers :isVideoLibrary="isVideoLibrary || isSearchModule" :getLayerStyle="getLayerStyle" />

            <!-- Marcadores de unidades -->
            <UnitMarkers v-if="!isSearchModule" :units="filteredUnitsOnline" :map="map" :screenBounds="globals.screenBounds"
                :getLayerStyle="getLayerStyle" />

            <!-- Capas de Mission Plans -->
            <MissionMarkers v-if="isMissionPlan || viewCurrentMissionPlan" />
            <MissionEventMarkers v-if="isMissionPlan || viewCurrentMissionEvents" @event-selected="selectMarker" />
    </l-map>
    <!-- Cruz punteada en el centro solo en móvil -->
    <div v-if="isMobile" class="center-cross"></div>
    </div>
    <!-- FIN MAIN MAP -->
</template>

<script setup lang="ts">
import L from 'leaflet'
globalThis.L = L

/* Importaciones */
import { ref, watch, computed, onMounted } from 'vue';
import { useGlobalStore } from '@/stores/global';
import { useServicesStore } from '@/stores/services';
import { storeToRefs } from 'pinia';
import "leaflet/dist/leaflet.css";
import 'vue-leaflet-markercluster/dist/style.css'
import { LMap, LTileLayer, LIcon, LMarker, LTooltip, LGeoJson } from "@vue-leaflet/vue-leaflet";
import type { Evento, EventsFilter, CoordenadasCenter, UnitOnline } from '../types';
import UnitMarkers from './UnitMarkers.vue'
import PhotoMarkers from './PhotoMarkers.vue'
import MosaicMarkers from './MosaicMarkers.vue'
import VideoMarkers from './VideoMarkers.vue'
import MissionMarkers from './MissionMarkers.vue'
import MissionEventMarkers from './MissionEventMarkers.vue'
import OwnEventsMarkers from './OwnEventsMarkers.vue';
import API from '../services'
import { useUtils } from '@/composables/useUtils'
import { useMosaics } from '@/composables/useMosaics'

/* Props */
const props = defineProps({
    mapa: { type: String, required: true },
    center: { type: Object as () => CoordenadasCenter, required: true }
})

/* Eventos emitidos */
const emit = defineEmits<{
    (event: 'eventSelected', seleccionado: Evento): void
    (event: 'crearEvento', coordenadas: [number, number]): void
    (event: 'mapClick', coordenadas: [number, number]): void
    (event: 'update-center', center: [number, number]): void
}>()

/* Variables reactivas */
// Referencia al componente TileLayer y lMap
const tileLayer = ref<InstanceType<typeof LTileLayer> | null>(null)
const map = ref<InstanceType<typeof LMap> | null>(null)

const localCenter = ref(props.center);
const iconSize = ref<[number, number]>([35, 35]);
const iconAnchor = ref<[number, number]>([16, 37]);

const onMapReady = () => {
    if (tileLayer.value?.leafletObject && map.value?.leafletObject) {
        // Configuración crítica para el zoom
        const leafletTileLayer = tileLayer.value.leafletObject
        leafletTileLayer.options.maxNativeZoom = 18
        leafletTileLayer.options.maxZoom = 22 // Mayor que maxNativeZoom para permitir zoom sobrepuesto

        // Forzar actualización del tile layer
        leafletTileLayer.setUrl(layer.value.url)

        // Opcional: Configura el maxZoom del mapa también
        map.value.leafletObject.setMaxZoom(22)
    }
}

const layer = ref({
    url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
    attribution: '&copy; OpenStreetMap contributors',
    name: 'OpenStreetMap.Mapnik'
})
/* Tiendas globales */
const globals = useGlobalStore();
const serv = useServicesStore();
const { prediosLayer, regionesLayer, comunasLayer, ciudadesLayer,
    carreterasLayer, isEvents, loadedPhotos, isImageLibrary,
    isVideoLibrary, isMissionPlan, viewCurrentMissionPlan, viewCurrentMissionEvents,isEmbeddedInMobileApp,
    isMobile, latestMosaics, zoom, selectedMosaicId, isMosaicLibrary, viewLatestMosaic, isSearchModule} =
    storeToRefs(globals);

const { eventTypes, predios, events, regiones, comunas, ciudades, carreteras,
    photoFiles, unitsOnline } = storeToRefs(serv);



/* Computed: Filtrar eventos visibles */
const filteredEvents = computed(() => {
    return events.value.filter((evt) => evt.user_visible == 1)
})

/* Filtrar unitsOnline por rol = 1 o 3 */
const filteredUnitsOnline = ref<UnitOnline[]>([])

const showPatrullas = ref(false)
const showAviones = ref(false)
const showBases = ref(false)
const showLatestMosaicPermission = ref(false)

// Variable reactiva para las fotos del mosaico seleccionado
const selectedMosaicPhotos = ref<any[]>([])

// Usar el composable de mosaicos
const { getMosaicPhotos, loadLatestMosaics, loadHMosaicPhotos } = useMosaics()

/* Observadores */
watch(
    () => props.center,
    (newCenter) => {
        // Solo actualiza si el valor realmente cambió
        if (!Array.isArray(newCenter) || !Array.isArray(localCenter.value) || newCenter[0] !== localCenter.value[0] || newCenter[1] !== localCenter.value[1]) {
            localCenter.value = [...newCenter]
        }
    },
    { immediate: true }
)

// Emitir evento cuando el centro cambia (por movimiento del mapa)
watch(
    () => localCenter.value,
    (newCenter) => {
        // Asegura que newCenter sea un array [number, number]
        let safeCenter: [number, number] = [0, 0];
        if (Array.isArray(newCenter) && newCenter.length === 2 && typeof newCenter[0] === 'number' && typeof newCenter[1] === 'number') {
            safeCenter = [newCenter[0], newCenter[1]];
        } else if (typeof newCenter === 'object' && newCenter !== null && 'lat' in newCenter && 'lng' in newCenter) {
            safeCenter = [Number(newCenter.lat), Number(newCenter.lng)];
        }
        // Solo emite si el valor realmente cambió respecto a props.center
        if (!Array.isArray(props.center) || safeCenter[0] !== props.center[0] || safeCenter[1] !== props.center[1]) {
            emit('update-center', safeCenter);
        }
    }
)

watch(
    () => props.mapa,
    async (newMap) => {
        changeMap(newMap)
    }
)
watch(
    () => unitsOnline.value,
    () => {
        updateFilteredUnitsOnline()
    },
    { immediate: true }
)

// Watch para cargar fotos del mosaico seleccionado
watch(
    () => selectedMosaicId.value,
    async (newMosaicId) => {
        if (newMosaicId !== null) {
            selectedMosaicPhotos.value = await loadHMosaicPhotos(newMosaicId)
        } else {
            selectedMosaicPhotos.value = []
        }
    },
    { immediate: true }
)


/* Funciones */

function getLayerStyle(type: string) {
    return () => {
        switch (type) {
            case 'camPolygon':
                return { color: "#6b9fda", weight: 1, fillOpacity: 0.3 };
            case 'predios':
                return { color: "green", weight: 1, fillOpacity: 0.3 };
            case 'regiones':
                return { color: "#0000ff", weight: 2, fillOpacity: 0.3 };
            case 'comunas':
                return { color: "#0000ff", weight: 1, fillOpacity: 0.3 };
            case 'ciudades':
                return { color: "green", weight: 1, fillOpacity: 0.3 };
            case 'carreteras':
                return { color: "red", weight: 1, fillOpacity: 0.3 };
            default:
                return { color: "#888", weight: 1, fillOpacity: 0.2 };
        }
    };
}
function handleMapClick(event: any) {
    const coords: [number, number] = [event.latlng.lat, event.latlng.lng]
    console.log('Click en el mapa:', coords)
    emit('mapClick', coords)
}
function addMarker(event: any) {
    if(!isEmbeddedInMobileApp.value){
       emit('crearEvento', [event.latlng.lat, event.latlng.lng])
    }
}

function selectMarker(eventId: number) {
    const selectedEvent = events.value.find(({ event_id }) => event_id === eventId)
    if (selectedEvent) {
        emit('eventSelected', selectedEvent)
    }
}
function selectOwnMarker(selectedEvent: any) {
    if (selectedEvent) {
        emit('eventSelected', selectedEvent)
    }
}
function getTypeId(type: string) {
    let tipoEvento = eventTypes.value.find((tipo) => tipo.event_type === type)

    return tipoEvento ? tipoEvento.event_type_id : 5
}
function changeMap(tipoMapa: string) {
    if (tipoMapa === 'OpenStreetMap') {
        layer.value = {
            url: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
            attribution: '&copy; OpenStreetMap contributors',
            name: 'OpenStreetMap.Mapnik'
        }
    } else {
        layer.value = {
            url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
            attribution:
                'Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye,Getmapping, Aerogrid, IGN, IGP, UPR- EGP, and the GIS User Community',
            name: 'Esri.WorldImagery'
        }
    }
}

async function callApi() {
    const filtros: EventsFilter = { visible: '1' }
    serv.dispatchGetEvents(filtros)
    //serv.dispatchListPhotos({});
}

function handleMouseOver(event: any) {
    globals.predio = event.layer.feature.properties.name
}

function handleMouseOut() {
    globals.predio = ""
}

let lastMouseOverTime = 0
function mouseOverMap(event: any) {
    const now = Date.now()
    if (now - lastMouseOverTime >= 200) {
        lastMouseOverTime = now
        globals.setElevation(event.latlng.lat, event.latlng.lng)
        globals.coordenadas = [event.latlng.lat, event.latlng.lng]
    }
}

function boundsChanged(bounds: any) {
    globals.screenBounds = bounds
    /* serv.dispatchGetRegiones(
          bounds._northEast.lng,
          bounds._northEast.lat,
          bounds._southWest.lng,
          bounds._southWest.lat
      ); */
}

async function updateFilteredUnitsOnline() {
    const now = Date.now()
    const roles: number[] = [];
    if (showPatrullas.value) roles.push(15);
    if (showBases.value) roles.push(3);
    if (showAviones.value) roles.push(1);
    // Si no hay ninguno, usar los originales (1 y 3)
    if (roles.length === 0) roles.push(1, 3);
    const units = Array.isArray(unitsOnline.value)
        ? unitsOnline.value
            .filter(unit =>
                roles.includes(unit.rol) &&
                unit.location &&
                Array.isArray(unit.location.coordinates) &&
                unit.location.coordinates.length === 2 &&
                typeof unit.location.coordinates[0] === 'number' &&
                typeof unit.location.coordinates[1] === 'number'
            )
        : []

    const result: UnitOnline[] = await Promise.all(units.map(async (unit) => {
        let estado = 0
        let extra: Partial<UnitOnline> = {}

        if (unit.utc) {
            const unitTime = parseUtcString(unit.utc).getTime()
            if (!isNaN(unitTime) && Math.abs(now - unitTime) < 60000) {
                estado = 1

                // Llamar al servicio getMissionDetails
                try {
                    const response = await API.getMissionDetails(unit.mission)
                    const entry = response?.data?.Entries?.Entry

                    if (entry) {
                        extra = {
                            copilot: entry.copilot ?? null,
                            pilot: entry.pilot ?? "",
                            mission_utc: entry.utc ?? "",
                            user_name: entry.user_name ?? "",
                            mission_id: entry.mission_id ?? null,
                            camera_sn: entry.camera_sn ?? null,
                            mission_version: entry.version ?? "",
                            duration: entry.duration ?? null,
                            observer: entry.observer ?? null,
                            operator2: entry.operator2 ?? null,
                            operator3: entry.operator3 ?? null,
                            operator1: entry.operator1 ?? null,
                            camera: entry.camera ?? "",
                            supervisor: entry.supervisor ?? null,
                            status: entry.status ?? null
                        }
                    } else {
                        extra = {
                            copilot: null,
                            pilot: "",
                            mission_utc: "",
                            user_name: "",
                            mission_id: null,
                            camera_sn: null,
                            mission_version: "",
                            duration: null,
                            observer: null,
                            operator2: null,
                            operator3: null,
                            operator1: null,
                            camera: "",
                            supervisor: null,
                            status: null
                        }
                    }
                } catch (error) {
                    extra = {
                        copilot: null,
                        pilot: "",
                        mission_utc: "",
                        user_name: "",
                        mission_id: null,
                        camera_sn: null,
                        mission_version: "",
                        duration: null,
                        observer: null,
                        operator2: null,
                        operator3: null,
                        operator1: null,
                        camera: "",
                        supervisor: null,
                        status: null
                    }
                }
            }
        }

        // Retornar el objeto UnitOnline completo
        return {
            ...unit,
            estado,
            ...extra
        } as UnitOnline
    }))

    filteredUnitsOnline.value = result
}
function parseUtcString(utcStr: string) {
    // Convierte "2025-06-13 16:57:50.009" a "2025-06-13T16:57:50.009Z"
    return new Date(utcStr.replace(' ', 'T') + 'Z');
}


/* Llamada inicial a la API */
onMounted(async () => {
    showPatrullas.value = await API.checkPermiso('SHOW_PATRULLAS')
    showAviones.value = await API.checkPermiso('SHOW_AVIONES')
    showBases.value = await API.checkPermiso('SHOW_BASES')
    showLatestMosaicPermission.value = await API.checkPermiso('SHOW_LATEST_MOSAIC')
    callApi();
    loadLatestMosaics(120);
});

// Exponer la referencia del mapa para uso externo
defineExpose({
    map
})

</script>

<style lang="css" scoped>
.map {
    width: 100%;
    height: 100vh;
    position: relative;
    z-index: 10;
}


@media (max-width: 680px) {
        .map {
                height: calc(100vh - 80px);
        }
}

/* Cruz punteada centro móvil */
.center-cross {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 36px;
    height: 36px;
    pointer-events: none;
    transform: translate(-50%, -50%);
    z-index: 1000;
}
.center-cross::before, .center-cross::after {
    content: '';
    position: absolute;
    background: repeating-linear-gradient(
        to right,
        #333 0 2px,
        transparent 2px 6px
    );
}
.center-cross::before {
    left: 50%;
    top: 0;
    width: 2px;
    height: 100%;
    transform: translateX(-50%);
}
.center-cross::after {
    top: 50%;
    left: 0;
    width: 100%;
    height: 2px;
    transform: translateY(-50%);
}

.icono {
    width: 32px;
    height: 32px;
}

.unidad {
    width: 48px;
    height: 48px;
}

span.point-head {
    background-color: #d1b31ed9;
    display: block;
    font-size: 8px;
    text-align: center;
}

.point-icon {
    position: relative;
    border-radius: 10px 0;
    overflow: hidden;
    height: 25px;
}

.pointicon.outside {
    border: 2px dashed red;
}

.leaflet-marker-icon.point-event.leaflet-zoom-animated.leaflet-interactive {
    width: 35px !important;
}

.pointicon .bi-airplane {
    filter: drop-shadow(1px 1px 1px rgba(50, 50, 50, 0.8));
}
.center-cross {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 36px;
    height: 36px;
    pointer-events: none;
    transform: translate(-50%, -50%);
    z-index: 1000;
}
.center-cross::before, .center-cross::after {
    content: '';
    position: absolute;
    background: repeating-linear-gradient(
        to right,
        #333 0 2px,
        transparent 2px 6px
    );
}
.center-cross::before {
    left: 50%;
    top: 0;
    width: 2px;
    height: 100%;
    transform: translateX(-50%);
}
.center-cross::after {
    top: 50%;
    left: 0;
    width: 100%;
    height: 2px;
    transform: translateY(-50%);
}

.center-cross {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 36px;
    height: 36px;
    pointer-events: none;
    transform: translate(-50%, -50%);
    z-index: 1000;
}
.center-cross::before, .center-cross::after {
    content: '';
    position: absolute;
    background: repeating-linear-gradient(
        to right,
        #333 0 2px,
        transparent 2px 6px
    );
}
.center-cross::before {
    left: 50%;
    top: 0;
    width: 2px;
    height: 100%;
    transform: translateX(-50%);
}
.center-cross::after {
    top: 50%;
    left: 0;
    width: 100%;
    height: 2px;
    transform: translateY(-50%);
}

/* Estilos para superponer el fondo debajo del icono */
.icono-wrapper {
    position: relative;
    display: inline-block;
    width: 32px;
    height: 32px;
}

.fondo-icono {
    position: absolute;
    top: 0;
    left: 0;
    width: 32px;
    height: 32px;
    z-index: 1;
}

.icono {
    position: relative;
    z-index: 2;
    position: relative;
}
</style>
